import { Alert, Button, setAlpha, TextArea } from '@talos/kyoko';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

const pulsatingAnimation = keyframes`
  0%,
  100% {
    opacity: 0.5;
  }
  33%, 50% {
    opacity: 1;
  }
`;

const loadingGlowPulse = keyframes`
  0%,
  100% {
    background-size: 100% 80%;
    opacity: 0.3;
  }
  50% {
    background-size: 120% 200%;
    opacity: 1;
  }
`;

const loadingGlowOverlay = css`
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background-image: radial-gradient(
    ellipse 100% 100% at 50% 0%,
    ${({ theme }) => setAlpha(0.1, theme.colors.gray['100'])} 0%,
    transparent 100%
  );
  background-repeat: no-repeat;
  background-position: 50% 0%;
  background-size: 70% 50%;
`;

const borderTravel = keyframes`
  to {
    --ava-ai-border-angle: 360deg;
  }
`;

/** Registers the angle so the loading border segment can travel without rotating the composer. */
export const AvaAIGlobalStyles = createGlobalStyle`
  @property --ava-ai-border-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
`;

const loadingGlowAnimation = css`
  ${loadingGlowOverlay}
  animation: ${loadingGlowPulse} 3s ease-in-out infinite;
`;

const travelingBorder = css`
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  padding: 2px;
  pointer-events: none;
  background: conic-gradient(
    from var(--ava-ai-border-angle),
    transparent 0deg,
    transparent 260deg,
    ${({ theme }) => setAlpha(0.35, theme.colors.primary.lighten)} 300deg,
    ${({ theme }) => setAlpha(0.5, theme.colors.gray['100'])} 332deg,
    ${({ theme }) => setAlpha(0.35, theme.colors.primary.lighten)} 360deg
  );
  filter: drop-shadow(0 0 20px ${({ theme }) => setAlpha(0.85, theme.colors.blue.lighten)});
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  animation: ${borderTravel} 3s linear infinite;
`;

export const AvaAIContentWrapper = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colorTextDefault};
  background-color: ${({ theme }) => theme.backgroundDrawer};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image: ${({ theme }) =>
      `radial-gradient(ellipse 90% 55% at 50% 25%, ${setAlpha(0.1, theme.colors.gray['100'])} 0%, transparent 100%)`};
    opacity: 0.1;
    animation: ${pulsatingAnimation} 12s infinite;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const AvaAIBody = styled.div<{ $footerHeight: number }>`
  --ava-ai-footer-height: ${({ $footerHeight }) => $footerHeight}px;
  position: relative;
  display: grid;
  flex: 1 1 auto;
  min-height: 0;
  grid-template: minmax(0, 1fr) / minmax(0, 1fr);
`;

export const AvaAIMessages = styled.div`
  display: flex;
  grid-area: 1 / 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing12}px;
  min-height: 0;
  padding: ${({ theme }) => theme.spacing16}px;
  padding-bottom: var(--ava-ai-footer-height, 0px);
  overflow-y: auto;
`;

export const AvaAIWelcome = styled.p`
  margin: auto;
  color: ${({ theme }) => theme.colorTextDefault};
  font-size: ${({ theme }) => theme.fontSizeXl}rem;
  text-align: center;
`;

export const AvaAIMessage = styled.article<{ $isUser: boolean }>`
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  max-width: ${({ $isUser }) => ($isUser ? '90%' : '100%')};
  padding: ${({ theme, $isUser }) => `${theme.spacing8}px ${$isUser ? `${theme.spacing12}px` : 0}`};
  color: ${({ $isUser, theme }) => ($isUser ? theme.colors.white.default : theme.colorTextImportant)};
  background: ${({ $isUser, theme }) => ($isUser ? theme.backgroundPrimary : 'transparent')};
  border-radius: ${({ theme }) => theme.borderRadiusLarge}px;
  line-height: 1.4;
  overflow-wrap: anywhere;

  p,
  ul,
  ol,
  pre,
  blockquote {
    margin: 0 0 ${({ theme }) => theme.spacing8}px;
  }

  > :last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    padding-left: ${({ theme }) => theme.spacing16}px;
  }

  pre {
    padding: ${({ theme }) => theme.spacing8}px;
    overflow-x: auto;
    background: ${({ theme }) => theme.backgroundBody};
    border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  }

  code {
    font-family: monospace;
  }
`;

export const AvaAIMessagePlaceholder = styled(AvaAIMessage).attrs({ $isUser: false })`
  position: relative;
  width: 80%;
  min-height: ${({ theme }) => theme.spacing32}px;

  &::before {
    ${loadingGlowAnimation}
  }
`;

export const AvaAIFooter = styled.div`
  position: relative;
  display: flex;
  grid-area: 1 / 1;
  flex-direction: column;
  align-self: end;
  z-index: 1;
  padding-top: ${({ theme }) => theme.spacing32 * 2}px;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      to top,
      ${({ theme }) => theme.backgroundDrawer},
      ${({ theme }) => setAlpha(0, theme.backgroundDrawer)}
    );
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    mask-image: linear-gradient(to top, rgba(0, 0, 0, 1) 40%, rgba(0, 0, 0, 0) 100%);
    -webkit-mask-image: linear-gradient(to top, rgba(0, 0, 0, 1) 40%, rgba(0, 0, 0, 0) 100%);
  }

  > * {
    position: relative;
    z-index: 1;
    pointer-events: auto;
  }
`;

export const AvaAIError = styled(Alert)`
  flex: 0 0 auto;
  align-items: center;
  border-right: 0;
  border-left: 0;
  border-radius: 0;

  > span {
    flex: 1;
  }
`;

export const AvaAIComposer = styled.form`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  margin: 0 ${({ theme }) => theme.spacing16}px ${({ theme }) => theme.spacing16}px;

  background: ${({ theme }) => theme.backgroundInput};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: ${({ theme }) => theme.borderRadiusBig}px;
  box-shadow: ${({ theme }) => `0 -${theme.spacing4}px ${theme.spacing16}px ${theme.colors.black.mute}`};
  transition:
    box-shadow 200ms ease,
    border-color 200ms ease;
  border: 1px solid transparent;
  border-top-color: ${({ theme }) => theme.colors.white.mute};
  border-bottom-color: ${({ theme }) => theme.colors.black.mute};

  &::before {
    ${loadingGlowOverlay}
    opacity: 0;
  }

  &:focus-within {
    box-shadow: ${({ theme }) => `0 -${theme.spacing4}px ${theme.spacing24}px ${theme.colors.black.mute}`};
    border: 1px solid ${({ theme }) => theme.colors.gray['040']};
  }

  &[aria-busy='true'] {
    &,
    &:focus-within {
      border-top-color: ${({ theme }) => setAlpha(0.2, theme.colors.gray['100'])};
    }

    &::before {
      ${loadingGlowAnimation}
    }

    &::after {
      ${travelingBorder}
    }

    @media (prefers-reduced-motion: reduce) {
      &::before,
      &::after {
        animation: none;
      }
    }
  }
`;

export const AvaAIInput = styled(TextArea)`
  && {
    flex: 1 1 auto;
    width: auto;
    min-height: ${({ theme }) => theme.spacing32 * 2}px;
    background: transparent;
    border: 0;
    box-shadow: none;
  }

  &&:hover,
  &&:focus-within {
    border: 0;
    box-shadow: none;
  }

  && textarea {
    min-width: 0;
    min-height: ${({ theme }) => theme.spacing32 * 2}px;
    padding: ${({ theme }) => theme.spacing16}px;
    line-height: 1.4;
    resize: none;
  }
`;

export const AvaAISendButton = styled(Button)`
  flex: 0 0 auto;
  border-radius: ${({ theme }) => `max(0px, calc(${theme.borderRadiusBig}px - ${theme.spacing8}px))`};

  &&:focus-visible {
    box-shadow: ${({ theme }) => `0 0 0 2px ${theme.borderColorInputFocus}`};
  }
`;
